import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { groupService, ROLE_SOURCE_OPTIONS } from '../../../services/groupService';

/**
 * Create/edit a participant group: name, description, dynamic role sources,
 * and individually-added members. A new group is created on first save so
 * members can then be added to it.
 */
const GroupEditor = ({ conferenceId, group, onClose, onChanged }) => {
  const { t } = useTranslation('notifications');
  const k = (key, opts) => t(`announcements.groupEditor.${key}`, opts);

  const [gid, setGid] = useState(group?.id || null);
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [roleSources, setRoleSources] = useState(new Set(group?.roleSources || []));
  const [savingDetails, setSavingDetails] = useState(false);
  const [members, setMembers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (gid) loadMembers();
    loadParticipants();
  }, [gid]);

  const loadMembers = async () => {
    try {
      setMembers(await groupService.getGroupMembers(gid));
    } catch (e) { console.error(e); }
  };

  const loadParticipants = async () => {
    try {
      setParticipants(await groupService.getConferenceParticipants(conferenceId));
    } catch (e) { console.error(e); }
  };

  const toggleRoleSource = (value) => {
    setRoleSources(prev => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };

  const handleSaveDetails = async () => {
    if (!name?.trim()) { setError(k('nameRequired')); return; }
    setSavingDetails(true);
    setError(null);
    try {
      const payload = { name, description, roleSources: Array.from(roleSources) };
      if (gid) {
        await groupService.updateGroup(gid, payload);
      } else {
        const created = await groupService.createGroup(conferenceId, payload);
        setGid(created?.id);
      }
      onChanged?.();
    } catch (e) {
      setError(e?.message || k('saveFailed'));
    } finally {
      setSavingDetails(false);
    }
  };

  const handleAddMember = async (userId) => {
    try {
      await groupService.addGroupMember(gid, userId);
      await loadMembers();
      onChanged?.();
    } catch (e) { setError(e?.message || k('addFailed')); }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await groupService.removeGroupMember(gid, userId);
      await loadMembers();
      onChanged?.();
    } catch (e) { setError(e?.message || k('removeFailed')); }
  };

  const memberIds = new Set(members?.map(m => m?.id));
  const filteredParticipants = participants
    ?.filter(p => !memberIds?.has(p?.id))
    ?.filter(p => {
      const q = search?.toLowerCase()?.trim();
      if (!q) return true;
      return `${p?.firstName} ${p?.lastName} ${p?.email}`.toLowerCase().includes(q);
    })
    ?.slice(0, 20);

  return (
    <div className="space-y-5">
      <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
        <Icon name="ChevronLeft" size={14} /> {k('back')}
      </button>

      <h3 className="text-lg font-semibold text-foreground">{gid ? k('editTitle') : k('newTitle')}</h3>

      {error && <p className="text-sm text-error">{error}</p>}

      {/* Details */}
      <div className="space-y-4 bg-card border border-border rounded-lg p-4">
        <Input label={k('name')} value={name} onChange={(e) => setName(e?.target?.value)} placeholder={k('namePlaceholder')} />
        <Input label={k('description')} value={description} onChange={(e) => setDescription(e?.target?.value)} placeholder={k('descriptionPlaceholder')} />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{k('roleSourcesLabel')}</label>
          <div className="flex flex-wrap gap-2">
            {ROLE_SOURCE_OPTIONS?.map(opt => {
              const active = roleSources?.has(opt?.value);
              return (
                <button
                  key={opt?.value}
                  type="button"
                  onClick={() => toggleRoleSource(opt?.value)}
                  className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                    active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:text-foreground'
                  }`}
                >
                  {active && <Icon name="Check" size={12} className="inline mr-1" />}
                  {k(`roleSourceOptions.${opt?.value}`)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSaveDetails} disabled={savingDetails} iconName="Save" iconPosition="left">
            {savingDetails ? k('saving') : gid ? k('saveChanges') : k('create')}
          </Button>
        </div>
      </div>

      {/* Individual members (only after the group exists) */}
      {gid ? (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-foreground">{k('membersTitle', { count: members?.length || 0 })}</h4>

          {members?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {members?.map(m => (
                <span key={m?.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-muted text-foreground">
                  {m?.firstName} {m?.lastName}
                  <button onClick={() => handleRemoveMember(m?.id)} className="text-muted-foreground hover:text-error">
                    <Icon name="X" size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <Input
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
            placeholder={k('searchPlaceholder')}
          />
          <div className="max-h-56 overflow-y-auto divide-y divide-border border border-border rounded-lg">
            {filteredParticipants?.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3">{k('noMatches')}</p>
            ) : (
              filteredParticipants?.map(p => (
                <div key={p?.id} className="flex items-center justify-between px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate">{p?.firstName} {p?.lastName}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {p?.email}{p?.roles?.length ? ` · ${p?.roles?.join(', ')}` : ''}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleAddMember(p?.id)} iconName="Plus" iconPosition="left">
                    {k('add')}
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{k('createFirstHint')}</p>
      )}
    </div>
  );
};

export default GroupEditor;
